// 🧾 Documentos Recibidos (SRI) — Financiero / Gestión SRI
// Lanza el proceso de descarga de comprobantes recibidos del portal SRI en Línea
// (lo ejecuta el worker sri-worker/) y muestra su progreso en vivo haciendo
// polling del RPC financiero.sri_job_estado.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Ban,
  CheckCircle2,
  FileDown,
  FileUp,
  Loader2,
  PlayCircle,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

const FINANCIERO_HEADERS = {
  'Content-Profile': 'financiero',
  'Accept-Profile': 'financiero',
};

const TIPOS_COMPROBANTE = [
  'Factura',
  'Liquidación de compra',
  'Nota de Crédito',
  'Nota de Débito',
  'Comprobante de Retención',
];

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

interface JobEstado {
  id: string;
  anio: number | null;
  mes: number | null;
  tipo_comprobante: string;
  estado: 'pendiente' | 'en_proceso' | 'completado' | 'error' | 'cancelado';
  total_documentos: number | null;
  descargados: number;
  omitidos: number;
  errores: number;
  mensaje_error: string | null;
}

interface JobEvento {
  id: number;
  nivel: 'info' | 'ok' | 'warn' | 'error';
  mensaje: string;
  created_at: string;
}

const ESTADO_FINALIZADO = ['completado', 'error', 'cancelado'];

// --- Importación por listado TXT del portal (script 427) ---------------------
// El portal SRI deja descargar un TXT tabulado con la clave de acceso de cada
// comprobante recibido; con eso el worker trae cada XML por el WEB SERVICE
// oficial (sin scraping — funciona aunque el portal esté caído).
interface MesCobertura {
  mes: string; documentos: number; sin_xml: number; con_error: number;
  ultima_carga: string | null; fuera_ventana_ws: boolean;
  estado: 'ok' | 'vacio' | 'incompleto';
}
interface Previsualizacion {
  total: number; nuevos: number; ya_estan: number; fuera_ventana_ws: number;
  desde: string | null; hasta: string | null; mensaje: string;
}

interface FilaTxt {
  ruc_emisor: string; razon_social_emisor: string; tipo_comprobante: string;
  serie_comprobante: string; clave_acceso: string;
  fecha_autorizacion: string | null; fecha_emision: string | null;
  num_doc_modificado: string | null;
}

const fechaSri = (s: string): string | null => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}:\d{2}:\d{2}))?/.exec((s || '').trim());
  return m ? `${m[3]}-${m[2]}-${m[1]}${m[4] ? ` ${m[4]}` : ''}` : null;
};

function parseTxtSri(texto: string): FilaTxt[] {
  const filas: FilaTxt[] = [];
  for (const linea of texto.split(/\r?\n/)) {
    const c = linea.split('\t');
    if (c.length < 5) continue;
    const clave = (c[4] || '').trim();
    if (!/^\d{49}$/.test(clave)) continue; // salta el encabezado y filas basura
    filas.push({
      ruc_emisor: (c[0] || '').trim(), razon_social_emisor: (c[1] || '').trim(),
      tipo_comprobante: (c[2] || '').trim(), serie_comprobante: (c[3] || '').trim(),
      clave_acceso: clave,
      fecha_autorizacion: fechaSri(c[5] || ''), fecha_emision: fechaSri(c[6] || ''),
      num_doc_modificado: (c[11] || '').trim() || null,
    });
  }
  return filas;
}

// El TXT del SRI suele venir en Windows-1252 (tildes rotas si se lee UTF-8)
async function leerTxt(archivo: File): Promise<string> {
  const buf = await archivo.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buf);
  } catch {
    return new TextDecoder('windows-1252').decode(buf);
  }
}

const ESTADO_BADGE: Record<JobEstado['estado'], { label: string; className: string }> = {
  pendiente: { label: 'En espera del worker', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  en_proceso: { label: 'En proceso', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  completado: { label: 'Completado', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  error: { label: 'Error', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  cancelado: { label: 'Cancelado', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
};

const NIVEL_CLASE: Record<JobEvento['nivel'], string> = {
  info: 'text-muted-foreground',
  ok: 'text-green-600 dark:text-green-400',
  warn: 'text-yellow-600 dark:text-yellow-400',
  error: 'text-red-600 dark:text-red-400',
};

export function DocumentosRecibidosSri() {
  const anioActual = new Date().getFullYear();
  const [anio, setAnio] = useState(anioActual);
  const [mes, setMes] = useState(new Date().getMonth() === 0 ? 12 : new Date().getMonth());
  const [tipo, setTipo] = useState(TIPOS_COMPROBANTE[0]);

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<JobEstado | null>(null);
  const [eventos, setEventos] = useState<JobEvento[]>([]);
  const [sinSenal, setSinSenal] = useState(false);  // worker no responde (pendiente demasiado tiempo)
  const ultimoEventoRef = useRef(0);
  const inicioRef = useRef(0);
  const logRef = useRef<HTMLDivElement>(null);

  const procesoActivo = !!jobId && (!job || !ESTADO_FINALIZADO.includes(job.estado));
  const SIN_SENAL_MS = 30000;

  const iniciarSeguimiento = useCallback((id: string) => {
    ultimoEventoRef.current = 0;
    inicioRef.current = Date.now();
    setSinSenal(false);
    setEventos([]);
    setJob(null);
    setJobId(id);
  }, []);

  // Al abrir la pantalla, retomar el proceso activo de la empresa si existe
  useEffect(() => {
    apiClient
      .post('/rpc/sri_job_activo', { _origen: 'recibidos' }, { headers: FINANCIERO_HEADERS })
      .then(({ data }) => {
        if (data?.id) iniciarSeguimiento(data.id);
      })
      .catch(() => {});
  }, [iniciarSeguimiento]);

  // Polling del estado + eventos nuevos mientras el proceso esté vivo
  useEffect(() => {
    if (!jobId) return;
    if (job && ESTADO_FINALIZADO.includes(job.estado)) return;

    let detenido = false;
    const consultar = async () => {
      try {
        const { data } = await apiClient.post(
          '/rpc/sri_job_estado',
          { _job_id: jobId, _desde_evento: ultimoEventoRef.current },
          { headers: FINANCIERO_HEADERS }
        );
        if (detenido || !data?.job) return;
        setJob(data.job);
        setSinSenal(data.job.estado === 'pendiente' && Date.now() - inicioRef.current > SIN_SENAL_MS);
        const nuevos: JobEvento[] = data.eventos || [];
        if (nuevos.length > 0) {
          ultimoEventoRef.current = nuevos[nuevos.length - 1].id;
          setEventos((prev) => [...prev, ...nuevos]);
        }
        if (ESTADO_FINALIZADO.includes(data.job.estado)) {
          if (data.job.estado === 'completado') {
            toast.success(`Proceso completado: ${data.job.descargados} descargados, ${data.job.omitidos} omitidos`);
          } else if (data.job.estado === 'error') {
            toast.error(`El proceso terminó con error: ${data.job.mensaje_error || 'ver el log'}`);
          }
        }
      } catch {
        // error transitorio de red: el siguiente tick reintenta
      }
    };

    void consultar();
    const intervalo = setInterval(consultar, 1500);
    return () => {
      detenido = true;
      clearInterval(intervalo);
    };
  }, [jobId, job?.estado]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autoscroll del log
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [eventos]);

  const crearJob = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(
        '/rpc/sri_crear_job',
        { _anio: anio, _mes: mes, _tipo_comprobante: tipo },
        { headers: FINANCIERO_HEADERS }
      );
      return data as { job_id: string };
    },
    onSuccess: (data) => {
      iniciarSeguimiento(data.job_id);
      toast.info('Proceso encolado, conectando con el SRI…');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'No se pudo iniciar el proceso');
    },
  });

  // Importar el listado TXT del portal (claves de acceso → XML por web service)
  const [filasTxt, setFilasTxt] = useState<FilaTxt[]>([]);
  const [nombreTxt, setNombreTxt] = useState('');
  const [previa, setPrevia] = useState<Previsualizacion | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  // Qué hay mes a mes: orienta ANTES de ir al portal a descargar el listado.
  const { data: cobertura = [] } = useQuery<MesCobertura[]>({
    queryKey: ['sri-cobertura'],
    queryFn: async () => (await apiClient.post('/rpc/sri_recibidos_cobertura',
      { _meses: 6 }, { headers: FINANCIERO_HEADERS })).data ?? [],
    staleTime: 0, refetchOnMount: 'always',
  });

  const elegirTxt = async (archivo: File | undefined) => {
    if (!archivo) return;
    try {
      const filas = parseTxtSri(await leerTxt(archivo));
      if (filas.length === 0) {
        toast.error('El archivo no tiene filas con clave de acceso válida (49 dígitos)');
        setFilasTxt([]); setNombreTxt('');
        return;
      }
      setFilasTxt(filas); setNombreTxt(archivo.name);
      // Antes de importar nada, decir qué va a pasar con ESTE archivo.
      try {
        const { data } = await apiClient.post('/rpc/sri_recibidos_previsualizar',
          { _claves: filas.map((f) => f.clave_acceso) }, { headers: FINANCIERO_HEADERS });
        setPrevia(data as Previsualizacion);
      } catch {
        setPrevia(null);
        toast.info(`${filas.length} comprobantes en el listado`);
      }
    } catch {
      toast.error('No se pudo leer el archivo');
    }
  };

  const importarTxt = useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(
        '/rpc/sri_crear_job_claves',
        { _filas: filasTxt },
        { headers: FINANCIERO_HEADERS }
      );
      return data as { job_id: string; total: number };
    },
    onSuccess: (data) => {
      iniciarSeguimiento(data.job_id);
      setFilasTxt([]); setNombreTxt(''); setPrevia(null);
      if (fileRef.current) fileRef.current.value = '';
      void qc.invalidateQueries({ queryKey: ['sri-cobertura'] });
      toast.info(`Proceso encolado: ${data.total} comprobantes por web service…`);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'No se pudo iniciar la importación');
    },
  });

  const cancelarJob = useMutation({
    mutationFn: async () => {
      await apiClient.post('/rpc/sri_cancelar_job', { _job_id: jobId }, { headers: FINANCIERO_HEADERS });
    },
    onSuccess: () => toast.warning('Cancelación solicitada'),
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'No se pudo cancelar');
    },
  });

  const badge = job ? ESTADO_BADGE[job.estado] : jobId ? ESTADO_BADGE.pendiente : null;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <FileDown className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Documentos Recibidos (SRI)</h1>
          <p className="text-sm text-muted-foreground">
            Descarga los comprobantes recibidos desde el portal SRI en Línea, o importa el listado TXT del portal (XML por web service)
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Período a procesar</CardTitle>
          <CardDescription>
            Se usan el RUC y la clave del SRI configurados en la empresa
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <label htmlFor="sri-anio" className="text-sm font-medium">Año</label>
              <select
                id="sri-anio"
                className="block h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={anio}
                onChange={(e) => setAnio(Number(e.target.value))}
                disabled={procesoActivo}
              >
                {Array.from({ length: anioActual - 2014 }, (_, i) => anioActual - i).map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="sri-mes" className="text-sm font-medium">Mes</label>
              <select
                id="sri-mes"
                className="block h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={mes}
                onChange={(e) => setMes(Number(e.target.value))}
                disabled={procesoActivo}
              >
                {MESES.map((nombre, i) => (
                  <option key={nombre} value={i + 1}>{nombre}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor="sri-tipo" className="text-sm font-medium">Tipo de comprobante</label>
              <select
                id="sri-tipo"
                className="block h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                disabled={procesoActivo}
              >
                {TIPOS_COMPROBANTE.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => crearJob.mutate()}
              disabled={procesoActivo}
              isLoading={crearJob.isPending}
            >
              {!crearJob.isPending && <PlayCircle className="w-4 h-4" />}
              Procesar
            </Button>
            {procesoActivo && (
              <Button
                variant="outline"
                onClick={() => cancelarJob.mutate()}
                isLoading={cancelarJob.isPending}
              >
                {!cancelarJob.isPending && <Ban className="w-4 h-4" />}
                Cancelar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {cobertura.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Qué tienes de cada mes</CardTitle>
            <CardDescription>
              Antes de ir al portal: los meses vacíos o incompletos son los que conviene descargar.
              Pasados ~3 meses el web service del SRI ya no conserva el XML.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {cobertura.map((m) => {
                const cls = m.estado === 'vacio'
                  ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20'
                  : m.estado === 'incompleto'
                    ? 'border-red-400 bg-red-50 dark:bg-red-900/20'
                    : 'border-input bg-muted/40';
                return (
                  <div key={m.mes} className={`rounded-md border px-3 py-2 text-sm ${cls}`}
                       title={m.ultima_carga ? `Última carga: ${new Date(m.ultima_carga).toLocaleString(LOCALE)}` : 'Sin cargas'}>
                    <div className="font-mono text-xs text-muted-foreground">{m.mes}</div>
                    <div className="font-semibold tabular-nums">{m.documentos}</div>
                    {m.sin_xml > 0 && (
                      <div className="text-xs text-red-600">{m.sin_xml} sin XML</div>
                    )}
                    {m.fuera_ventana_ws && (
                      <div className="text-[11px] text-muted-foreground">fuera del WS</div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileUp className="w-5 h-5" /> Importar listado del SRI (TXT)
          </CardTitle>
          <CardDescription>
            En el portal SRI (Comprobantes recibidos) usa &quot;Descargar listado&quot; para obtener un archivo
            .txt con la clave de acceso de cada comprobante. Súbelo aquí: el sistema trae cada XML por el
            <strong> web service oficial</strong> — sirve incluso cuando la consulta del portal falla.
            El web service solo conserva los comprobantes recientes (~3 meses) y no entrega el RIDE (PDF).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            <input
              ref={fileRef}
              type="file"
              accept=".txt,.tsv,text/plain"
              className="block text-sm file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-2 file:text-sm file:font-medium hover:file:bg-muted/70"
              disabled={procesoActivo}
              onChange={(e) => void elegirTxt(e.target.files?.[0])}
            />
            {filasTxt.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {nombreTxt}: <strong>{filasTxt.length}</strong> comprobantes
              </span>
            )}
            <Button
              onClick={() => importarTxt.mutate()}
              disabled={procesoActivo || filasTxt.length === 0 || previa?.nuevos === 0}
              isLoading={importarTxt.isPending}
            >
              {!importarTxt.isPending && <FileUp className="w-4 h-4" />}
              Importar {previa ? `${previa.nuevos} nuevo(s)` : filasTxt.length > 0 ? `${filasTxt.length} comprobantes` : ''}
            </Button>
          </div>

          {/* Qué va a pasar con ESTE archivo, antes de lanzar nada. */}
          {previa && (
            <div className="mt-4 rounded-md border p-3 text-sm">
              <div className="flex flex-wrap gap-x-6 gap-y-1">
                <span>En el archivo: <strong className="tabular-nums">{previa.total}</strong></span>
                <span className="text-green-700 dark:text-green-400">
                  Nuevos: <strong className="tabular-nums">{previa.nuevos}</strong>
                </span>
                <span className="text-muted-foreground">
                  Ya estaban: <strong className="tabular-nums">{previa.ya_estan}</strong>
                </span>
                {previa.desde && (
                  <span className="text-muted-foreground">
                    Período: {previa.desde} → {previa.hasta}
                  </span>
                )}
              </div>
              {previa.fuera_ventana_ws > 0 && (
                <div className="mt-2 flex items-start gap-2 text-amber-700 dark:text-amber-400">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    <strong className="tabular-nums">{previa.fuera_ventana_ws}</strong> tienen más de
                    3 meses: el web service del SRI ya no conserva su XML, así que entrarán sin
                    detalle. Se importan igual para que el comprobante exista, pero no se podrán
                    procesar por concepto.
                  </span>
                </div>
              )}
              {previa.nuevos === 0 && (
                <p className="mt-2 text-muted-foreground">{previa.mensaje}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {jobId && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Progreso del proceso</CardTitle>
              {badge && (
                <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${badge.className}`}>
                  {procesoActivo && <Loader2 className="w-3 h-3 animate-spin" />}
                  {job?.estado === 'completado' && <CheckCircle2 className="w-3 h-3" />}
                  {job?.estado === 'error' && <AlertCircle className="w-3 h-3" />}
                  {badge.label}
                </span>
              )}
            </div>
            {job && (
              <CardDescription>
                {job.tipo_comprobante}
                {job.mes != null && ` · ${MESES[job.mes - 1]} ${job.anio}`}
                {job.total_documentos !== null && ` · ${job.total_documentos} documentos`}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {sinSenal && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/15 dark:text-amber-300">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <p className="font-medium">El procesador no está respondiendo</p>
                  <p className="text-xs">El proceso lleva más de 30 s en espera y nadie lo ha tomado. El worker de descarga (sri-worker) podría estar detenido — contacta al administrador. El proceso quedará encolado y se ejecutará en cuanto el worker vuelva.</p>
                </div>
              </div>
            )}
            {job && (
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-semibold text-green-600 dark:text-green-400">{job.descargados}</p>
                  <p className="text-xs text-muted-foreground">Descargados</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-semibold">{job.omitidos}</p>
                  <p className="text-xs text-muted-foreground">Omitidos (ya existían)</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-semibold text-red-600 dark:text-red-400">{job.errores}</p>
                  <p className="text-xs text-muted-foreground">Con error</p>
                </div>
              </div>
            )}

            <div
              ref={logRef}
              className="h-72 overflow-y-auto rounded-md border bg-muted/30 p-3 font-mono text-xs space-y-1"
            >
              {eventos.length === 0 && (
                <p className="text-muted-foreground">Esperando eventos del proceso…</p>
              )}
              {eventos.map((evento) => (
                <p key={evento.id} className={NIVEL_CLASE[evento.nivel]}>
                  <span className="opacity-60">
                    {new Date(evento.created_at).toLocaleTimeString(LOCALE, { hour12: false })}
                  </span>{' '}
                  {evento.mensaje}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
